import React, { useEffect, useState } from 'react';

import { Datepicker, SpinnerOverlay, SubmissionButtons } from '@components/common';
import FlatTree from '@components/common/FlatTree';

import { useMutateExportCrowdCellConfig } from '@hooks/crowdCells/useMutateExportCrowdCellConfig';
import { useQueryCrowdCellConfig } from '@hooks/crowdCells/useQueryCrowdCellConfig';
import { useQueryCrowdCellDatesExist } from '@hooks/crowdCells/useQueryCrowdCellDatesExist';

import { startOfDayInUTC } from '@utils/kpi-utils/date';
import { convertToFlatTreeData } from '@components/common/FlatTree/utils';

import { TTechnologiesValues } from '@enums/common';

import styles from './NodeConfiguration.module.scss';

interface INodeConfigurationProps {
	closeModal: () => void;
	crowdCellId: string;
	date: number | null;
	technology: TTechnologiesValues | undefined;
}

const NodeConfiguration = ({ crowdCellId, technology, date, closeModal }: INodeConfigurationProps) => {
	const [timestamp, setTimestamp] = useState<number | undefined>();

	const {
		crowdCellConfigDateExists,
		isInitialLoadingCrowdCellConfigDateExists: isLoadingCrowdCellDate,
	} = useQueryCrowdCellDatesExist({
		crowdCellId,
		type: technology,
	});

	const {
		crowdCellConfig,
		isFetchingCrowdCellConfig,
	} = useQueryCrowdCellConfig({
		crowdCellId,
		type: technology,
		timestamp,
	});

	const { handleDownloadCrowdCellConfig } = useMutateExportCrowdCellConfig([closeModal]);

	const treeData = crowdCellConfig?.config
		? convertToFlatTreeData(JSON.parse(crowdCellConfig.config))
		: [];

	useEffect(() => {
		setTimestamp(date || crowdCellConfigDateExists?.at(-1) || undefined);
	}, [crowdCellConfigDateExists, date]);

	const exportHandler = () => {
		if (!timestamp) return;

		handleDownloadCrowdCellConfig({
			crowdCellId,
			type: technology,
			timestamp: startOfDayInUTC(new Date(timestamp!)),
		});
	};

	const datePickerChangeHandler = (date: number | null) => setTimestamp(date!);

	return (
		<div className={styles.configurationWrapper}>
			{(isLoadingCrowdCellDate || isFetchingCrowdCellConfig) && <SpinnerOverlay />}
			<div className={styles.dateWrapper}>
				<span>Choose date</span>
				<Datepicker
					disableFutureDates
					disabled={!crowdCellConfigDateExists?.length}
					handleChange={datePickerChangeHandler}
					includeDates={crowdCellConfigDateExists as any[]}
					isMobileDevice={false}
					isTodayButton={false}
					selected={timestamp ? new Date(timestamp) : null}
				/>
			</div>
			{crowdCellConfig?.config && (
				<div className={styles.treeWrapper}>
					<FlatTree
						isReadOnly
						treeData={treeData}
					/>
				</div>
			)}
			<SubmissionButtons
				disabledPositive={!timestamp}
				negativeHandler={closeModal}
				positiveHandler={exportHandler}
				positiveLabel='Export'
			/>
		</div>
	);
};

export default NodeConfiguration;
